import React, { useEffect, useState, useCallback } from 'react'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import InputLabel from '@material-ui/core/InputLabel'
import Input from '@material-ui/core/Input'
import withStyles from '@material-ui/core/styles/withStyles'
import { withApi } from '~shared/api/ApiContext'
import FieldFactory from '~components/atom/FieldFactory/FieldFactory'
import classNames from 'classnames'

const styles = theme => ({
  root: {},
  tableWrapper: {
    overflowX: 'scroll',
  },
  delete: {
    width: '1px',
  },
  addButton: {
    marginTop: 5,
  },
})

const customStyles = ({ spacing: { unit } }) => ({
  root: {
    height: unit * 4,
    maxHeight: unit * 4,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    overflow: 'hidden',
  },
  rootBoolean: {
    alignItems: 'flex-start',
  },
  underline: {
    padding: `0 ${unit * 0.5}px`,
    boxSizing: 'border-box',
    bottom: '-1px',
    '&:before': {
      borderBottomColor: 'transparent',
    },
  },
  select: {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },
  label: {
    display: 'flex',
    justifyContent: 'center',
  },
})

const CustomTableField = withStyles(customStyles)(({ item, onChange, classes, ...rest }) => {
  let padding = 'none'
  let extraProps = {
    label: '',
    name: '',
    originalName: item.name,
    onChange: e => {
      onChange &&
        onChange({
          target: e.target,
          ref: null,
        })
    },
    fullWidth: true,
    helperText: '',
    isInlined: true,
    classes: {
      root: classes.root,
    },
    input: <Input classes={{ underline: classes.underline }} />,
    InputProps: {
      classes: {
        root: classes.root,
        underline: classes.underline,
      },
    },
  }

  switch (item.type) {
    case 'enum':
      extraProps.classes.select = classes.select
      break
    case 'reference':
    case 'boolean':
      extraProps.classes.root = classNames(classes.root, classes.rootBoolean)
      padding = 'dense'
      break
    default:
      break
  }

  return (
    <TableCell {...rest} padding={padding}>
      {FieldFactory.getFieldByType({
        ...item,
        ...extraProps,
      })}
    </TableCell>
  )
})

const ApiTable = ({ classes, className, disabled, value, history, label, onChange, name, api }) => {
  const [options, setOptions] = useState(undefined)

  useEffect(() => {
    let isMounted = true
    const fetchOptions = async () => {
      if (!api) return
      let { request } = api.getAlternativeIdsOptions()
      try {
        const opts = await request
        if (isMounted) setOptions(opts)
      } catch (err) {
        // ignore
      }
    }
    fetchOptions()
    return () => {
      isMounted = false
    }
  }, [api])

  const getFakeEvent = useCallback(
    (rowData, columnData = value?.columnData) => ({
      target: {
        name,
        type: 'table',
        value: {
          rowData,
          columnData,
        },
      },
    }),
    [name, value],
  )

  const updateRow = useCallback(
    (row, col, rowIndex, e) => {
      const prevRowData = value?.rowData || []
      const rowData = [...prevRowData]
      row = { ...row }
      row[col.name] = e.target.value
      rowData.splice(rowIndex, 1, row)
      return rowData
    },
    [value],
  )

  const handleChange = useCallback(
    (col, rowIndex) => e => {
      const rowDataProps = value?.rowData || []
      const row = rowDataProps[rowIndex]
      const rowData = updateRow(row, col, rowIndex, e)
      const fakeEvent = getFakeEvent(rowData)
      onChange && onChange(fakeEvent)
    },
    [onChange, value, updateRow, getFakeEvent],
  )

  if (!value) return null

  const { columnData, rowData } = value

  return (
    <div className={classNames(classes.root, className)}>
      <InputLabel style={{ position: 'inherit' }} shrink>
        {label}
      </InputLabel>
      <div className={classes.tableWrapper}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <TableRow>
              <TableCell className={classes.delete} padding="none" />
              {columnData &&
                columnData.map(col => {
                  const { name: colName, label: colLabel } = col
                  return (
                    <TableCell
                      key={colName}
                      numeric={col.numeric}
                      padding="none"
                      className={classes.head}
                    >
                      {colLabel || colName}
                    </TableCell>
                  )
                })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowData &&
              rowData.map(({ _link, ...row }, rowIndex) => (
                <TableRow onClick={_link && history && (() => history.push(_link))} key={rowIndex}>
                  <TableCell padding="none" className={classes.cell}></TableCell>
                  {columnData.map(col => (
                    <CustomTableField
                      item={{
                        ...col,
                        disabled: col.disabled || row[col.name] === null || disabled,
                        value: row[col.name],
                        options:
                          options && row?.group && options[row.group] ? options[row.group] : [],
                      }}
                      key={`${rowIndex}_${col.name}`}
                      classes={classes}
                      onChange={handleChange(col, rowIndex)}
                    />
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default withApi(withStyles(styles)(ApiTable))
export { CustomTableField }
